/* eslint-disable react/prop-types */
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import {
  formatTimestampToAgo,
  getDenmarkMediaUrlWithSeopath,
  getHostLogoStyleByHostId,
  openVideoInNewTab,
  socialPublishStatusToTextAndColor,
  createHostOptions,
  getSocialChannelImage,
  THUMBNAIL_SIZE_SUFFIX,
} from "../../utils";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { PublishedIcon, toast } from "../../components";
import { AllHostsInfo, AllHostsList, MediaModel } from "@/database";
import { Host } from "@/store/media/media.models";
import { Tooltip } from "../../components";
import {
  callDenmarkDetailApi,
  callDenmarkArticleGetApi,
} from "@/store/upload/upload.action";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@/store/index";
import noImage from "../../assets/icons/svg/no-image.svg";
import DenmarkPublishPopover from "../MediaPublish/DenmarkPublish/DenmarkPublishPopover";
import DenmarkEditModal from "../MediaPublish/DenmarkPublish/DenmarkEditModal";
import DenmarkMultiPublishChips from "../MediaPublish/DenmarkPublish/DenmarkMultiPublishChips";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";
import { useNavigate } from "react-router-dom";
import { renderStatusIndicator } from "@/utils/mediautils/RenderStatusIndicator";
import { trackEvent, getTrackUserContext } from "@/utils/analytics";
import { getContentLocation } from "@/utils/utilFunctions/commmonFunctions";
import { store } from "@/store";

/** Stamp multi-publish child Denmark rows with their scheduled parent’s `schedule` (for list chips). */
function applyParentScheduleToMultiPublishChildren<
  T extends {
    section?: number;
    schedule?: number;
    status?: number;
    job_type?: string;
    multi_publish_targets?: { section?: number }[];
  },
>(items: T[]): T[] {
  const childSectionToSchedule = new Map<number, number>();
  for (const parent of items) {
    if (parent.job_type !== "denmark") continue;
    if (parent.status !== 10) continue;
    if (parent.schedule == null || Number(parent.schedule) <= Date.now()) {
      continue;
    }
    const targets = parent.multi_publish_targets;
    if (!Array.isArray(targets) || targets.length === 0) continue;
    const pSched = Number(parent.schedule);
    const pSection = Number(parent.section);
    for (const t of targets) {
      const cs = Number(t.section);
      if (Number.isNaN(cs) || cs === pSection) continue;
      childSectionToSchedule.set(cs, pSched);
    }
  }
  if (childSectionToSchedule.size === 0) return items;
  return items.map((item) => {
    if (item.job_type !== "denmark" || item.status !== 10) return item;
    const inherited = childSectionToSchedule.get(Number(item.section));
    if (inherited == null) return item;
    return { ...item, schedule: inherited };
  });
}

const rotatingStyles = `
  @keyframes rotate {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(0deg); }
  }
  .rotating-sandbox {
    animation: rotate 2s infinite linear;
    display: inline-block;
    width: 16px;
    height: 16px;
  }
`;

export const RenderPublishedTo: React.FC<{
  media: MediaModel;
  allHostsList?: AllHostsList;
  activeType: string;
  getProductNameById: (productId: string) => string;
  hosts?: Host[];
  denmarkHosts?: AllHostsInfo[];
}> = ({
  media,
  allHostsList,
  activeType,
  getProductNameById,
  hosts,
  denmarkHosts,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const socialChannelsList = useAppSelector(
    (s) => s.app.userSocialChannels.socialChannelsList
  );
  const [popoverChip, setPopoverChip] = useState<{
    chip: any;
    anchor: DOMRect;
  } | null>(null);
  const [denmarkEditModalOpen, setDenmarkEditModalOpen] = useState(false);
  const [denmarkEditData, setDenmarkEditData] =
    useState<DenmarkPublishInterface | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const contentLocation = getContentLocation();

  const handleDenmarkEdit = (publishData: DenmarkPublishInterface) => {
    setDenmarkEditData(publishData);
    setDenmarkEditModalOpen(true);
    setPopoverChip(null);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setPopoverChip(null);
    }
  };

  const hostOptions = createHostOptions(hosts, denmarkHosts);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const scheduledTimes = media.socials
      ?.filter((s) => s.schedule && s.schedule > now && s.status === 10)
      .map((s) => s.schedule as number);

    if (!scheduledTimes || scheduledTimes.length === 0) return;

    const nearest = Math.min(...scheduledTimes);
    const delay = nearest - Date.now();

    // If the scheduled time has already passed relative to current time,
    // immediately bump `now` so that the UI can switch from clock to status.
    if (delay <= 0) {
      setNow(Date.now());
      return;
    }

    const timer = setTimeout(() => setNow(Date.now()), delay + 500);
    return () => clearTimeout(timer);
  }, [media.socials, now]);

  const socialsWithInheritedSchedules = useMemo(
    () => applyParentScheduleToMultiPublishChildren(media.socials ?? []),
    [media.socials]
  );

  const visibleItems =
    socialsWithInheritedSchedules.length > 0 &&
    socialsWithInheritedSchedules.slice(0, 3);
  const remainingItems =
    socialsWithInheritedSchedules.length > 0 &&
    socialsWithInheritedSchedules.slice(3);

  //TODO- Have to replace Platform name from Channel Name

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = rotatingStyles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const renderMoreBtn = () => (
    <div className="col-6 mt-1">
      <div className="d-flex justify-content-start align-items-center">
        <div
          className="bg-secondary"
          style={{ height: "2px", width: "10px" }}
        ></div>
        <div
          className="text-center cursor-pointer rounded-1 px-1"
          style={{ width: "60px" }}
          onClick={() => navigate(`/media/${media.id}/details`)}
        >
          More <i className="bi bi-caret-right-fill H"></i>
        </div>
        <div
          className="bg-secondary"
          style={{ height: "2px", width: "10px" }}
        ></div>
      </div>
    </div>
  );

  const handleLaunchVideo = async (
    job_type: string,
    published_id?: string | undefined,
    status?: number,
    channelName?: string | undefined,
    hostId?: number
  ) => {
    if (status !== 10) return;
    if ((job_type === "denmark" || job_type === "denmark_embed") && hostId) {
      const isArticle = job_type === "denmark_embed";
      const url = await getDenmarkMediaUrlWithSeopath(
        published_id || "",
        hostId,
        dispatch,
        callDenmarkDetailApi,
        isArticle,
        callDenmarkArticleGetApi,
        allHostsList
      );
      if (url) {
        window.open(url, "_blank");
      } else {
        toast.fail("Live URL could not be fetched");
      }
    } else {
      openVideoInNewTab(job_type, published_id, channelName);
    }
  };

  const handleRouting = () => {
    if (popoverChip?.chip.status === 10) {
      handleLaunchVideo(
        popoverChip.chip.job_type,
        popoverChip.chip.published_id,
        popoverChip.chip.status,
        popoverChip.chip.channel_name,
        popoverChip.chip.host
      );
    }
  };

  const getUniqueTeams = useCallback(
    (childMedia: any[] | undefined) => {
      if (!childMedia || childMedia.length === 0) return [];
      const uniqueTeams = childMedia
        .filter(
          (item: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.team === item.team)
        )
        .map((item: any) => getProductNameById(item.team));
      return uniqueTeams;
    },
    [getProductNameById]
  );

  const uniqueTeamsDisplay = useMemo(() => {
    if (activeType === DRIVE_TAB) {
      return getUniqueTeams(media.child_media);
    }
    return [];
  }, [activeType, media.child_media, getUniqueTeams]);

  return media?.status !== -2 ? (
    <div
      ref={containerRef}
      className="position-relative d-flex flex-wrap gap-2"
    >
      {visibleItems &&
        visibleItems?.map((item, index) => {
          const channelImg = getSocialChannelImage(item, socialChannelsList, {
            sizeSuffix: THUMBNAIL_SIZE_SUFFIX,
          });
          return (
            <div key={item.id + index} style={{ maxWidth: "50%" }}>
              <div
                className="social-chip d-flex align-items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  trackEvent("browse", {
                    category: "content",
                    entity: "Publish Chip",
                    action: "click",
                    location: contentLocation,
                    ...getTrackUserContext(),
                  });
                  const rect = (
                    e.currentTarget as HTMLElement
                  ).getBoundingClientRect();
                  const isSameChip =
                    popoverChip?.chip.id === item.id ||
                    (popoverChip?.chip.channel_name &&
                      popoverChip?.chip.channel_name === item.channel_name);
                  if (isSameChip) {
                    setPopoverChip(null);
                  } else {
                    setPopoverChip({
                      chip: { ...item },
                      anchor: rect,
                    });
                  }
                }}
              >
                {item.job_type === "denmark" ||
                item.job_type === "denmark_embed" ? (
                  <span
                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden"
                    style={{ width: "14px", height: "14px" }}
                  >
                    <div
                      style={{
                        ...getHostLogoStyleByHostId(
                          item.host,
                          hosts,
                          hostOptions,
                          denmarkHosts
                        ),
                        zoom: "0.44",
                      }}
                    />
                  </span>
                ) : channelImg ? (
                  <img
                    src={channelImg}
                    alt={item.channel_name || item.job_type}
                    width={14}
                    height={14}
                    className="rounded-circle flex-shrink-0 bg-light"
                    style={{ objectFit: "cover" }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = noImage;
                    }}
                  />
                ) : (
                  <PublishedIcon
                    job_type={item.job_type}
                    status={item.status}
                  />
                )}
                <span
                  className="text-truncate mx-1 d-inline-block me-1 align-middle"
                  style={{
                    maxWidth:
                      item.job_type === "denmark" ||
                      item.job_type === "denmark_embed"
                        ? "200px"
                        : "70%",
                  }}
                >
                  {item.channel_name}
                </span>
                <Tooltip
                  content={
                    item?.schedule && item.schedule > now && item.status === 10
                      ? `Scheduled On: ${new Date(
                          item.schedule
                        ).toLocaleString()}`
                      : item?.status === 10
                        ? `Published On: ${formatTimestampToAgo(
                            item?.schedule || item?.mt
                          )}`
                        : item?.status === -2 || item?.status === -1
                          ? (item?.msg ?? "Your content has failed to publish.")
                          : item?.status === 0
                            ? "Your content is in queue, uploading will begin once content is activated. "
                            : item?.status === -7
                              ? "This content has been soft deleted."
                              : item?.status === -4
                                ? "This content has been permanently deleted."
                                : "Your content is getting uploaded, currently in progress."
                  }
                >
                  {item?.schedule &&
                  item.schedule > now &&
                  item.status === 10 ? (
                    <i className="bi bi-clock text-secondary me-1 fs-6"></i>
                  ) : (
                    renderStatusIndicator(item.status)
                  )}
                </Tooltip>
              </div>
            </div>
          );
        })}
      {activeType === DRIVE_TAB &&
        uniqueTeamsDisplay.length > 0 &&
        uniqueTeamsDisplay.map((teamName, index) => (
          <span key={`team-${index}`} className="social-chip me-1 mb-1">
            <i className="bi bi-people-fill text-primary me-1 small"></i>
            <span className="text-truncate" style={{ maxWidth: "120px" }}>
              {teamName}
            </span>
          </span>
        ))}
      {activeType === DRIVE_TAB &&
        uniqueTeamsDisplay.length === 0 &&
        !visibleItems && <span>--</span>}
      <DenmarkMultiPublishChips
        socials={media.socials || []}
        hosts={hosts}
        hostOptions={hostOptions}
        denmarkHosts={denmarkHosts}
        isDraft={false}
        showPopover={true}
        usePublishedIcon={false}
        compactLogo={true}
        onChipClick={handleDenmarkEdit}
        onPopoverChipClick={(target, parent, anchor) => {
          const isSameChip = popoverChip?.chip.section === target.section;
          if (isSameChip) {
            setPopoverChip(null);
          } else {
            setPopoverChip({
              chip: {
                ...parent,
                channel_name: target.channel_name,
                section: target.section,
              },
              anchor,
            });
          }
        }}
      />
      {remainingItems && remainingItems.length > 0 && renderMoreBtn()}
      {denmarkEditModalOpen && denmarkEditData && (
        <DenmarkEditModal
          isOpen={denmarkEditModalOpen}
          onClose={() => {
            setDenmarkEditModalOpen(false);
            setDenmarkEditData(null);
          }}
          publishData={denmarkEditData}
          hosts={hosts}
          hostOptions={hostOptions}
          denmarkHosts={denmarkHosts}
        />
      )}
      {popoverChip && (
        <DenmarkPublishPopover
          chip={{ ...popoverChip.chip }}
          anchor={popoverChip.anchor}
          onClose={() => setPopoverChip(null)}
          handleRouting={handleRouting}
          hostLogoStyle={getHostLogoStyleByHostId(
            popoverChip.chip.host,
            hosts,
            hostOptions,
            denmarkHosts
          )}
          publishData={{ ...popoverChip.chip }}
          currentMediaDetailsForEdit={media}
          hosts={hosts}
          hostOptions={hostOptions}
          denmarkHosts={denmarkHosts}
          onEdit={handleDenmarkEdit}
        />
      )}
    </div>
  ) : (
    "--"
  );
};
